import { type Database } from "@fitness-app/supabase";

// TRE-2413 — drinks logged from the "Dodaj napój" sheet, one row per entry. The daily
// hydration_measurement row keeps `portion` as the sum of every drink and stays the source of
// truth for the progress bar, the goal and the charts; this table only explains that sum.
// Water is always derived (portion minus the other drinks), so water added by the quick +/-,
// from client-web or before this table existed is still counted without an entry here.

export type HydrationEntryEntity = Database["public"]["Tables"]["hydration_entry"]["Row"];

export enum DrinkType {
  Water = "water",
  Coffee = "coffee",
  Tea = "tea",
  Other = "other",
}

export const DRINK_TYPES: DrinkType[] = [DrinkType.Water, DrinkType.Coffee, DrinkType.Tea, DrinkType.Other];

export interface HydrationEntry extends Omit<HydrationEntryEntity, "drinkType"> {
  drinkType: DrinkType;
}

/** Ready-made portions offered in the sheet, in ml. */
export const DRINK_PRESETS_ML: Record<DrinkType, number[]> = {
  [DrinkType.Water]: [250, 330, 500, 750],
  [DrinkType.Coffee]: [30, 100, 200, 300],
  [DrinkType.Tea]: [200, 250, 350],
  [DrinkType.Other]: [200, 330, 500],
};

export type HydrationBreakdown = Record<DrinkType, number>;

/**
 * Splits a day's hydration total into drinks. Coffee, tea and other come from the entries;
 * water is what remains of the total, never below zero (client-web can lower the total without
 * knowing about the entries).
 */
export const getHydrationBreakdown = (
  portion: number,
  entries: Pick<HydrationEntry, "drinkType" | "amountMl">[],
): HydrationBreakdown => {
  const breakdown: HydrationBreakdown = {
    [DrinkType.Water]: 0,
    [DrinkType.Coffee]: 0,
    [DrinkType.Tea]: 0,
    [DrinkType.Other]: 0,
  };

  for (const entry of entries) {
    if (entry.drinkType !== DrinkType.Water && entry.drinkType in breakdown) {
      breakdown[entry.drinkType] += entry.amountMl;
    }
  }

  breakdown[DrinkType.Water] = Math.max(
    0,
    portion - breakdown[DrinkType.Coffee] - breakdown[DrinkType.Tea] - breakdown[DrinkType.Other],
  );

  return breakdown;
};
